import { useState } from "react";
import { Gift, CheckCircle2, Sparkles, Star, Trophy, X as CloseIcon, Users, ArrowRight, Info } from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import SEO from "@/components/SEO";

const GutscheinGewinnenPage = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string; terms?: string }>({});
  const { toast } = useToast();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { name?: string; email?: string; terms?: string } = {};

    if (!name.trim()) {
      newErrors.name = "Bitte gib deinen Namen ein.";
    }
    if (!email.trim()) {
      newErrors.email = "Bitte gib deine E-Mail-Adresse ein.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Bitte gib eine gültige E-Mail-Adresse ein.";
    }
    if (!acceptTerms) {
      newErrors.terms = "Bitte akzeptiere die Teilnahmebedingungen.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitted(true);
    toast({
      title: "Erfolgreich teilgenommen! 🎉",
      description: "Viel Glück bei der Verlosung! Wir benachrichtigen dich per E-Mail, falls du gewinnst.",
    });
  };

  const prizes = [
    {
      icon: <Trophy className="h-10 w-10" />,
      title: "2. Platz",
      amount: "50€",
      description: "Restaurant-Gutschein",
      validity: "12 Monate gültig",
      color: "from-gray-300 to-gray-400",
      glow: "shadow-gray-400/30",
      order: 1 // Links
    },
    {
      icon: <Trophy className="h-14 w-14" />,
      title: "1. Platz",
      amount: "100€",
      description: "Restaurant-Gutschein",
      validity: "12 Monate gültig",
      color: "from-yellow-400 to-orange-500",
      glow: "shadow-yellow-500/50",
      order: 0, // Mitte (größer)
      isWinner: true
    },
    {
      icon: <Star className="h-8 w-8" />,
      title: "3. Platz",
      amount: "25€",
      description: "Restaurant-Gutschein",
      validity: "12 Monate gültig",
      color: "from-orange-400 to-red-500",
      glow: "shadow-orange-500/30",
      order: 2 // Rechts
    }
  ];

  // Sortiere Preise für Podium: 2. Platz links, 1. Platz Mitte, 3. Platz rechts
  const sortedPrizes = [...prizes].sort((a, b) => a.order - b.order);

  const participatingRestaurants = [
    { id: 1, name: "Ristorante Bella", placeholder: true },
    { id: 2, name: "Bistro am Markt", placeholder: true },
    { id: 3, name: "Asia Wok", placeholder: true },
    { id: 4, name: "Burger House", placeholder: true },
    { id: 5, name: "Pizza Express", placeholder: true },
    { id: 6, name: "Steakhouse Premium", placeholder: true }
  ];

  const additionalRestaurantsCount = 24; // Variable für weitere Restaurants

  const faqs = [
    {
      question: "Wie oft kann ich teilnehmen?",
      answer: "Jede Person kann einmal pro Monat am Gewinnspiel teilnehmen. Mehrfachteilnahmen werden nicht berücksichtigt."
    },
    {
      question: "Wann werden die Gewinner bekannt gegeben?",
      answer: "Die Verlosung findet monatlich statt. Gewinner werden innerhalb von 7 Tagen nach der Verlosung per E-Mail benachrichtigt."
    },
    {
      question: "Wo kann ich die Gutscheine einlösen?",
      answer: "Die Gutscheine sind in allen teilnehmenden Restaurants auf SpeiseGURU einlösbar. Eine Liste findest du weiter unten auf dieser Seite."
    },
    {
      question: "Wie lange sind die Gutscheine gültig?",
      answer: "Alle Gutscheine haben eine Gültigkeitsdauer von 12 Monaten ab Ausstellungsdatum. Das Ablaufdatum findest du auf dem Gutschein."
    },
    {
      question: "Was passiert mit meinen Daten?",
      answer: "Deine Daten werden ausschließlich für die Durchführung des Gewinnspiels verwendet. Nach Ablauf der Gültigkeit der Gutscheine werden die Daten gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen. Weitere Informationen findest du in unserer Datenschutzerklärung."
    },
    {
      question: "Kann ich den Gutschein übertragen?",
      answer: "Die Gutscheine sind nicht übertragbar und nur vom Gewinner einlösbar. Eine Barauszahlung ist nicht möglich."
    }
  ];

  const keyFaqs = faqs.slice(0, 3);
  const remainingFaqs = faqs.slice(3);

  // Gewinner werden innerhalb von 7 Tagen benachrichtigt
  const winnerNotificationDays = 7;

  return (
    <main className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <SEO 
        title="Restaurant-Gutschein gewinnen | SpeiseGuru"
        description="Nimm teil und gewinne Restaurant-Gutscheine im Wert von bis zu 100€! Kostenlos teilnehmen, dauert 10 Sekunden."
      />
      {/* Hero Section - Premium */}
      <section className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white overflow-hidden">
        {/* Grain Texture Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}></div>
        
        {/* Soft Glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl opacity-60"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center animate-fade-in-up">
            {/* Gift Icon - größer mit Glow */}
            <div className="inline-block mb-6 relative">
              <div className="absolute inset-0 bg-yellow-300/30 rounded-full blur-2xl"></div>
              <Gift className="h-24 w-24 mx-auto text-yellow-300 relative z-10 drop-shadow-2xl" />
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold mb-6 tracking-tight leading-[1.15] pb-2">
              <span className="inline-block">Gutschein</span>{" "}
              <span className="inline-block bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
                gewinnen
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-4 max-w-3xl mx-auto leading-relaxed">
              Nimm teil und gewinne Restaurant-Gutscheine im Wert von bis zu 100€!
            </p>

            {/* Microcopy */}
            <p className="text-base md:text-lg text-white/80 mb-6 leading-relaxed">
              Kostenlos teilnehmen • dauert 10 Sekunden • Gewinner per E-Mail
            </p>

            {/* Monatliche Verlosung Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 mb-8">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              <span className="text-sm font-semibold">Monatliche Verlosung</span>
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            </div>
          </div>
        </div>
      </section>

      {/* Prizes Section - Podium Design (Brand-Anker: Grün als Akzent) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 -mt-10 overflow-visible">
        {/* Mobile: Horizontal Scroll Podium */}
        <div className="md:hidden mb-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4">
          <div className="flex gap-4 w-max">
            {sortedPrizes.map((prize, index) => (
              <div
                key={index}
                className="w-[85vw] max-w-[320px] flex-shrink-0 snap-center"
              >
                <article className="relative animate-fade-in-up overflow-visible">
                  {/* Wrapper für Badge (overflow-visible) */}
                  <div className="relative overflow-visible">
                    <div className={`bg-gradient-to-br ${prize.color} rounded-3xl p-6 text-white shadow-2xl ${prize.glow} transform transition-colors duration-300 h-full flex flex-col relative min-h-[240px]`}>
                      {/* Crown/Glow für 1. Platz */}
                      {prize.isWinner && (
                        <>
                          <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-primary/20 rounded-full blur-xl z-0"></div>
                          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                            <div className="bg-primary text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg border-2 border-white/20">
                              TOP!
                            </div>
                          </div>
                        </>
                      )}
                      
                      <div className="flex items-center justify-center mb-4 mt-2">
                        {prize.icon}
                      </div>
                      <div className="text-center flex-grow flex flex-col justify-center">
                        <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-2">
                          <h3 className="text-lg font-bold">{prize.title}</h3>
                        </div>
                        <div className={`font-extrabold mb-2 ${prize.isWinner ? "text-4xl" : "text-3xl"}`}>
                          {prize.amount}
                        </div>
                        <p className="text-white/90 mb-1 text-base font-semibold">{prize.description}</p>
                        <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-2 py-0.5 mt-1">
                          <p className="text-white/90 text-xs font-medium">{prize.validity}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: Grid Podium */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8 items-end overflow-visible">
          {sortedPrizes.map((prize, index) => (
            <article
              key={index}
              className={`relative animate-fade-in-up overflow-visible ${
                prize.isWinner 
                  ? "md:scale-110 md:z-10 md:-mt-4" 
                  : "md:scale-95"
              } ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
            >
              {/* Wrapper für Badge (overflow-visible) */}
              <div className="relative overflow-visible pt-4">
                <div className={`bg-gradient-to-br ${prize.color} rounded-3xl p-8 text-white shadow-2xl ${prize.glow} transform hover:scale-105 transition-colors duration-300 h-full flex flex-col relative ${
                  prize.isWinner ? "min-h-[360px] ring-4 ring-primary/30" : "min-h-[280px]"
                }`}>
                  {/* Crown/Glow für 1. Platz */}
                  {prize.isWinner && (
                    <>
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-primary/20 rounded-full blur-xl z-0"></div>
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                        <div className="bg-primary text-white px-5 py-1.5 rounded-full text-sm font-bold shadow-lg border-2 border-white/20">
                          TOP!
                        </div>
                      </div>
                    </>
                  )}
                
                <div className="flex items-center justify-center mb-6">
                  {prize.icon}
                </div>
                <div className="text-center flex-grow flex flex-col justify-center">
                  {/* Text-Hierarchie klar */}
                  <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 mb-3">
                    <h3 className="text-xl md:text-2xl font-bold">{prize.title}</h3>
                  </div>
                  <div className={`font-extrabold mb-3 ${prize.isWinner ? "text-5xl md:text-6xl" : "text-4xl md:text-5xl"}`}>
                    {prize.amount}
                  </div>
                  <p className="text-white/90 mb-2 text-lg font-semibold">{prize.description}</p>
                  <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mt-2">
                    <p className="text-white/90 text-sm font-medium">{prize.validity}</p>
                  </div>
                </div>
              </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Form Section */}
      <section id="teilnahme" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 overflow-visible">
        {!isSubmitted ? (
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 animate-fade-in-up overflow-visible">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Jetzt teilnehmen
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Fülle das Formular aus und nimm an der Verlosung teil
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-base font-semibold text-gray-900 mb-2">
                  Dein Name *
                </label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors({ ...errors, name: undefined });
                  }}
                  placeholder="Max Mustermann"
                  className={`w-full h-12 px-4 text-base border-2 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                    errors.name ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                  }`}
                  required
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-base font-semibold text-gray-900 mb-2">
                  Deine E-Mail-Adresse *
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: undefined });
                  }}
                  placeholder="max@example.com"
                  className={`w-full h-12 px-4 text-base border-2 rounded-xl focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
                    errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-gray-300"
                  }`}
                  required
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                  <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Trust-Hinweis als neutrales Info-Badge */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                <Info className="h-4 w-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>1 Teilnahme pro Person/Monat.</strong> Gewinner werden per E-Mail benachrichtigt. Teilnahme kostenlos.
                </p>
              </div>

              {/* Checkbox für Teilnahmebedingungen */}
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => {
                      setAcceptTerms(checked === true);
                      if (errors.terms) setErrors({ ...errors, terms: undefined });
                    }}
                    className={`mt-1 ${errors.terms ? "border-red-500" : ""}`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.terms}
                  />
                  <label htmlFor="terms" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    Ich akzeptiere die{" "}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="text-primary hover:text-primary-dark underline font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                        >
                          Teilnahmebedingungen
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Teilnahmebedingungen</DialogTitle>
                          <DialogDescription>
                            Bitte lese die Teilnahmebedingungen sorgfältig durch.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 space-y-4 text-gray-700 leading-relaxed">
                          <p>Die Teilnahme am Gewinnspiel ist kostenlos und unverbindlich.</p>
                          <p>Jede Person kann einmal pro Monat teilnehmen. Mehrfachteilnahmen werden nicht berücksichtigt.</p>
                          <p>Die Verlosung findet monatlich statt. Gewinner werden per E-Mail benachrichtigt.</p>
                          <p>Die Gutscheine sind 12 Monate gültig und in allen teilnehmenden Restaurants auf SpeiseGURU einlösbar.</p>
                          <p>Eine Barauszahlung oder Übertragung der Gutscheine ist nicht möglich.</p>
                          <p>Der Rechtsweg ist ausgeschlossen.</p>
                        </div>
                      </DialogContent>
                    </Dialog>
                    {" "}und die{" "}
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          className="text-primary hover:text-primary-dark underline font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                        >
                          Datenschutzerklärung
                        </button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Datenschutzerklärung</DialogTitle>
                          <DialogDescription>
                            Informationen zum Umgang mit deinen Daten.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="mt-4 space-y-4 text-gray-700 leading-relaxed">
                          <p>Deine Daten werden ausschließlich für die Durchführung des Gewinnspiels verwendet.</p>
                          <p>Nach Ablauf der Gültigkeit der Gutscheine werden die Daten gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.</p>
                          <p>Weitere Informationen findest du in unserer vollständigen{" "}
                            <Link href="/datenschutz" className="text-primary hover:text-primary-dark underline">
                              Datenschutzerklärung
                            </Link>.
                          </p>
                        </div>
                      </DialogContent>
                    </Dialog>
                    . *
                  </label>
                </div>
                {errors.terms && (
                  <p className="text-sm text-red-600 ml-7" role="alert">
                    {errors.terms}
                  </p>
                )}

                {/* Newsletter optisch leiser */}
                <div className="flex items-start gap-3 opacity-75">
                  <Checkbox
                    id="newsletter"
                    checked={newsletter}
                    onCheckedChange={(checked) => setNewsletter(checked === true)}
                    className="mt-1"
                  />
                  <label htmlFor="newsletter" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                    Ich möchte den SpeiseGURU Newsletter erhalten (optional)
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-gradient-to-r from-primary to-primary-light hover:from-primary-dark hover:to-primary text-white font-semibold py-6 rounded-xl text-lg shadow-lg hover:shadow-xl transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <Gift className="mr-2 h-5 w-5" aria-hidden="true" />
                Am Gewinnspiel teilnehmen
              </Button>
            </form>

            {/* Inline-FAQ als Accordion */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Häufige Fragen</h3>
              <Accordion type="single" collapsible className="w-full space-y-2">
                {keyFaqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="bg-gray-50 rounded-lg border border-gray-200 px-5 shadow-sm hover:shadow-md hover:border-primary/30 transition-colors duration-200"
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed pb-4 text-sm">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-primary to-primary-light rounded-3xl shadow-2xl p-12 md:p-16 text-center text-white animate-scale-in overflow-visible">
            <div className="mb-6">
              <CheckCircle2 className="h-20 w-20 mx-auto drop-shadow-lg" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Danke für deine Teilnahme! 🎉
            </h2>
            <p className="text-xl text-white/90 mb-2 max-w-2xl mx-auto leading-relaxed">
              Du bekommst eine Bestätigung per E-Mail.
            </p>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Gewinner werden innerhalb von <strong>{winnerNotificationDays} Tagen</strong> nach der Verlosung benachrichtigt.
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setEmail("");
                setName("");
                setAcceptTerms(false);
                setNewsletter(false);
                setErrors({});
              }}
              variant="outline"
              size="lg"
              className="bg-white text-primary hover:bg-white/95 rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
            >
              <CloseIcon className="mr-2 h-5 w-5" aria-hidden="true" />
              Schließen
            </Button>
          </div>
        )}
      </section>

      {/* Participating Restaurants Section */}
      <section className="bg-white border-y border-gray-200 py-20 overflow-visible">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Einlösbar bei teilnehmenden Restaurants
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed mb-2">
              Die Gutscheine können in allen teilnehmenden Restaurants auf SpeiseGURU eingelöst werden
            </p>
            <p className="text-base text-primary font-medium">
              Einlösbar in deiner Region
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {participatingRestaurants.map((restaurant) => (
              <div
                key={restaurant.id}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-200 hover:border-primary transition-colors duration-300 text-center min-h-[120px] flex flex-col items-center justify-center"
              >
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-3">
                  <Users className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
                <p className="text-sm font-medium text-gray-700 leading-tight">{restaurant.name}</p>
              </div>
            ))}
          </div>

          {/* + X weitere Restaurants */}
          <div className="text-center mb-8">
            <p className="text-base text-gray-600 font-medium">
              + {additionalRestaurantsCount} weitere Restaurants
            </p>
          </div>

          <div className="text-center">
            <Link href="/restaurants">
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-full px-8 py-6 font-semibold transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Alle teilnehmenden Restaurants ansehen
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section - Accordion */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 overflow-visible">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Weitere Fragen
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Alles, was du über das Gewinnspiel wissen musst
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-3">
          {remainingFaqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-white rounded-lg border border-gray-200 px-6 shadow-sm hover:shadow-md hover:border-primary/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-6 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-6 text-base">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA Leiste vor Legal */}
      <section className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-y border-primary/20 py-12 overflow-visible">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <p className="text-lg font-semibold text-gray-900">
              Bereit? Jetzt teilnehmen
            </p>
            <a href="#teilnahme">
              <Button
                size="lg"
                className="bg-primary text-white hover:bg-primary-dark rounded-full px-8 py-6 font-semibold shadow-lg hover:shadow-xl transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                Zur Teilnahme
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Legal / Transparency Section */}
      <section className="bg-gray-50 border-t border-gray-200 py-12 overflow-visible">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              Gewinner werden per E-Mail benachrichtigt. Teilnahme kostenlos.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <Link href="/impressum" className="text-primary hover:text-primary-dark font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md px-2 py-1">
              Teilnahmebedingungen
            </Link>
            <Link href="/datenschutz" className="text-primary hover:text-primary-dark font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md px-2 py-1">
              Datenschutz
            </Link>
            <Link href="/impressum" className="text-primary hover:text-primary-dark font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md px-2 py-1">
              Impressum
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default GutscheinGewinnenPage;
